import { label } from './labels';

/**
 * Строка списка по краткому описанию объявления (T11.10).
 *
 * Списков, показывающих объявления, в приложении два — избранное и
 * история уведомлений, — и оба получают от сервера одно и то же краткое
 * описание (`modules/listings/summary.ts`). Строка у них общая по той же
 * причине, по какой оно общее на сервере: две копии разошлись бы, и в
 * одном списке появилась бы цена со скидкой, а в другом нет.
 *
 * Выдача поиска сюда не входит намеренно. Она приходит из таблицы поиска
 * и устроена иначе: номера марок вместо названий, свой признак
 * просмотра. Подгонять их друг под друга значило бы завести общий тип,
 * половина полей которого всегда пуста.
 */

export interface Summary {
  readonly listing_id: string | number;
  readonly status: string;
  readonly source: { id: number; code: string; name: string };
  readonly url: string;
  readonly brand: { id: number; name: string } | null;
  readonly model: { id: number; name: string } | null;
  readonly year: number;
  readonly mileage_km: number | null;
  readonly price_minor: string | number;
  readonly currency: string;
  readonly region: { id: number; name: string };
  readonly published_at: string;
  readonly image_url: string | null;
  readonly market_delta_pct: number | null;
}

function rubles(minor: string | number): string {
  return `${Math.round(Number(minor) / 100).toLocaleString('ru-RU')} ₽`;
}

function mileage(km: number | null): string | null {
  if (km === null) return null;

  return km < 1000 ? `${String(km)} км` : `${String(Math.round(km / 1000))} тыс. км`;
}

/** Снятое объявление помечается, а не прячется: человек его отложил. */
const GONE: Record<string, string> = {
  removed: 'снято',
  sold: 'продано',
  expired: 'истекло',
};

export function SummaryRow({
  listing,
  onOpen,
  action,
}: {
  listing: Summary;
  onOpen: (listingId: string | number) => void;
  /** Правое действие строки: убрать из избранного, удалить уведомление. */
  action?: { readonly label: string; readonly onClick: () => void };
}): React.JSX.Element {
  const name = [listing.brand?.name, listing.model?.name]
    .filter((one): one is string => one !== undefined && one !== '')
    .join(' ');
  const facts = [mileage(listing.mileage_km), listing.region.name, label(listing.source.code)]
    .filter((one): one is string => one !== null)
    .join(' · ');
  const gone = GONE[listing.status];
  const delta = listing.market_delta_pct;

  return (
    <div className="card summary">
      {/*
        Кнопка — открывающая часть строки, а не строка целиком: рядом
        стоит второе действие, и строка-кнопка проглатывала бы нажатие
        по нему
      */}
      <button
        type="button"
        className="summary__main"
        onClick={() => {
          onOpen(listing.listing_id);
        }}
      >
        {listing.image_url === null ? null : (
          <img className="summary__photo" src={listing.image_url} alt="" loading="lazy" />
        )}

        <span className="summary__text">
          <span className="summary__name">
            {name === '' ? 'Автомобиль' : name}, {String(listing.year)}
            {gone === undefined ? '' : ` · ${gone}`}
          </span>
          <span className="summary__price">
            {rubles(listing.price_minor)}
            {delta === null || delta === 0 ? null : (
              <span className={delta < 0 ? 'result__delta result__delta--good' : 'result__delta'}>
                {delta > 0 ? '+' : '−'}
                {String(Math.abs(delta))} %
              </span>
            )}
          </span>
          {facts === '' ? null : <span className="summary__facts">{facts}</span>}
        </span>
      </button>

      {action === undefined ? null : (
        <button type="button" className="summary__action" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  );
}
